/**
 * Drawing state machine store.
 * Manages active tool, vertices, and shape configuration during drawing sessions.
 */

import { create } from 'zustand';
import type { DrawingTool, DrawingState, GeoPoint } from '@vtak/shared';
import { DRAWING_DEFAULTS } from '@vtak/shared';

/** Tool-specific configuration for the active drawing */
export interface ShapeConfig {
  callsign: string;
  strokeColor: number;
  strokeWeight: number;
  fillColor: number;
  /** Route-specific */
  planningMethod: string;
  routeMethod: string;
  direction: string;
  routeType: string;
  /** Geofence-specific */
  trigger: 'Entry' | 'Exit' | 'Both';
  monitor: string;
  tracking: boolean;
  elevationMonitored: boolean;
  minElevation: number;
  maxElevation: number;
  /** Measurement-specific */
  rangeUnits: number;
  bearingUnits: number;
  northRef: number;
}

/** Drawing store state and actions */
interface DrawingStoreState {
  activeTool: DrawingTool | null;
  drawingState: DrawingState;
  vertices: GeoPoint[];
  radiusMeters: number;
  shapeConfig: ShapeConfig;
  startTool: (tool: DrawingTool) => void;
  addVertex: (point: GeoPoint) => void;
  removeLastVertex: () => void;
  setRadius: (meters: number) => void;
  finishDrawing: () => void;
  cancelDrawing: () => void;
  setSending: () => void;
  setSent: (success: boolean) => void;
  updateConfig: (partial: Partial<ShapeConfig>) => void;
  reset: () => void;
}

/** Default shape configuration */
const DEFAULT_CONFIG: ShapeConfig = {
  callsign: '',
  strokeColor: DRAWING_DEFAULTS.STROKE_COLOR,
  strokeWeight: DRAWING_DEFAULTS.STROKE_WEIGHT,
  fillColor: DRAWING_DEFAULTS.FILL_COLOR,
  planningMethod: 'Infil',
  routeMethod: 'Driving',
  direction: 'Infil',
  routeType: 'Primary',
  trigger: 'Entry',
  monitor: 'All',
  tracking: false,
  elevationMonitored: false,
  minElevation: 0,
  maxElevation: 0,
  rangeUnits: 0,
  bearingUnits: 0,
  northRef: 0,
};

/** Zustand store for drawing interaction state */
export const useDrawingStore = create<DrawingStoreState>((set) => ({
  activeTool: null,
  drawingState: 'idle',
  vertices: [],
  radiusMeters: 0,
  shapeConfig: { ...DEFAULT_CONFIG },

  startTool: (tool) => set({
    activeTool: tool,
    drawingState: 'drawing',
    vertices: [],
    radiusMeters: 0,
    shapeConfig: { ...DEFAULT_CONFIG },
  }),

  addVertex: (point) => set((state) => ({
    vertices: [...state.vertices, point],
  })),

  removeLastVertex: () => set((state) => ({
    vertices: state.vertices.slice(0, -1),
  })),

  setRadius: (meters) => set({ radiusMeters: meters }),

  finishDrawing: () => set({ drawingState: 'confirming' }),

  cancelDrawing: () => set({
    activeTool: null,
    drawingState: 'idle',
    vertices: [],
    radiusMeters: 0,
  }),

  setSending: () => set({ drawingState: 'sending' }),

  setSent: (success) => set({
    drawingState: success ? 'sent' : 'confirming',
  }),

  updateConfig: (partial) => set((state) => ({
    shapeConfig: { ...state.shapeConfig, ...partial },
  })),

  reset: () => set({
    activeTool: null,
    drawingState: 'idle',
    vertices: [],
    radiusMeters: 0,
    shapeConfig: { ...DEFAULT_CONFIG },
  }),
}));
